import { SvgIcon } from '../../components/icons';
import { DsRoot } from '../ThemeProvider';
import { Mode, ThemeMeta } from '../themes';
import { STATUS_COLOR, usd, WARRANTY_CLAIMS } from '../data/warrantyDemo';
import { Button, Chip, SegmentedControl, StatusChip, Switch } from '../primitives';

type ThemePosterProps = {
  theme: ThemeMeta;
  mode?: Mode;
};

const ROWS = WARRANTY_CLAIMS.slice(0, 4);

/**
 * A miniature of the whole system in one theme: header, sidebar, buttons,
 * controls, chips and a slice of the table.
 *
 * This exists so the five directions can be compared honestly — same content,
 * same layout, only the tokens differ. Each poster is its own `DsRoot`, which
 * is exactly why the provider and the scoping element are separate components.
 */
export const ThemePoster = ({ theme, mode }: ThemePosterProps) => (
  <DsRoot
    theme={theme.id}
    mode={mode ?? theme.defaultMode}
    density="compact"
    style={{
      borderRadius: 12,
      overflow: 'hidden',
      border: '1px solid rgba(128,128,128,0.35)',
    }}
  >
    <div style={{ background: 'var(--ds-bg)' }}>
      {/* Header strip */}
      <div className="ds-header" style={{ paddingLeft: 10, paddingRight: 10 }}>
        <span className="ds-brand-mark" aria-hidden="true">
          MT
        </span>
        <span className="ds-brand" style={{ fontSize: 'var(--ds-fs-sm)' }}>
          Maintener
        </span>
        <span className="ml-auto flex items-center gap-1">
          <span className="ds-icon-btn" aria-hidden="true">
            <SvgIcon name="notifications" size={14} />
          </span>
          <span className="ds-icon-btn" aria-hidden="true">
            <SvgIcon name="settings" size={14} />
          </span>
        </span>
      </div>

      <div className="flex" style={{ minHeight: 232 }}>
        {/* Sidebar strip */}
        <div
          className="ds-sidebar"
          style={{ width: 128, borderRight: '1px solid var(--ds-sidebar-border)' }}
          aria-hidden="true"
        >
          <div className="ds-nav" style={{ padding: 5 }}>
            <div className="ds-nav-section-label">Operations</div>
            <div className="ds-nav-item">
              <span className="ds-nav-icon">
                <SvgIcon name="airlinesRounded" size={13} />
              </span>
              <span className="ds-nav-label" style={{ fontSize: 11 }}>
                Hangar
              </span>
            </div>
            <div className="ds-nav-item">
              <span className="ds-nav-icon">
                <SvgIcon name="flightTakeoff" size={13} />
              </span>
              <span className="ds-nav-label" style={{ fontSize: 11 }}>
                Line KEF
              </span>
            </div>
            <div className="ds-nav-section-label" style={{ marginTop: 8 }}>
              Requests
            </div>
            <div className="ds-nav-item" data-ds-active="true">
              <span className="ds-nav-icon">
                <SvgIcon name="description" size={13} />
              </span>
              <span className="ds-nav-label" style={{ fontSize: 11 }}>
                Warranty
              </span>
            </div>
            <div className="ds-nav-item">
              <span className="ds-nav-icon">
                <SvgIcon name="attachMoney" size={13} />
              </span>
              <span className="ds-nav-label" style={{ fontSize: 11 }}>
                Payments
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1" style={{ padding: 10 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
            <span className="ds-title" style={{ fontSize: 'var(--ds-fs-lg)' }}>
              Warranty Claims
            </span>
            <SegmentedControl
              label="Scope"
              value="all"
              onChange={() => {}}
              options={[
                { value: 'all', label: 'All' },
                { value: 'mine', label: 'Mine' },
              ]}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2" style={{ marginBottom: 8 }}>
            <Button variant="confirm" size="sm">
              Submit claim
            </Button>
            <Button variant="primary" size="sm">
              Primary
            </Button>
            <Button variant="secondary" size="sm">
              Secondary
            </Button>
            <Button variant="ghost" size="sm">
              Ghost
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2" style={{ marginBottom: 8 }}>
            <input
              className="ds-field"
              style={{ width: 128, height: 'var(--ds-control-h-sm)' }}
              placeholder="Search…"
              readOnly
            />
            <Chip tone="accent">LHT Contract</Chip>
            <Chip tone="ok">Credit</Chip>
            <Switch label="" defaultChecked readOnly />
          </div>

          {/* Table slice */}
          <div className="ds-card" data-ds-flush="true">
            <table className="ds-table">
              <thead>
                <tr>
                  <th>Tail</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r, i) => (
                  <tr key={r.warranty_id} data-ds-zebra={i % 2 ? 'true' : undefined}>
                    <td className="ds-mono">{r.tail_number}</td>
                    <td>
                      <StatusChip
                        color={STATUS_COLOR[r.warranty_status]}
                        label={r.warranty_status}
                      />
                    </td>
                    <td className="ds-td-num">{usd.format(r.total_claimed_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </DsRoot>
);
